/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { AGENCY_NAV_ITEMS } from './agency-nav.items';
import type { AgencyNavItem } from './agency-nav.types';
import type { PermissionAction } from '@/shared/utils/permissions.types';

export type {
  AgencyNavItem,
  AgencyNavSubItem,
  DesktopPlacement,
  MobilePlacement,
} from './agency-nav.types';
export { AGENCY_NAV_ITEMS } from './agency-nav.items';

/**
 * Whether an item with `children` still has at least one enabled child.
 *
 * Items carrying children render as expandable groups rather than links, so a
 * group whose every child is feature-flagged off is dead UI: it opens onto an
 * empty flyout and navigates nowhere. Childless items are unaffected.
 */
const hasEnabledChildren = (item: AgencyNavItem): boolean =>
  !item.children?.length
  || item.children.some((child) => (child.isEnabled ? child.isEnabled() : true));

/**
 * Whether a nav item is enabled — invokes its `isEnabled` predicate, defaulting
 * to enabled when none is provided, and hides groups left with no enabled child.
 */
const isItemEnabled = (item: AgencyNavItem): boolean =>
  (item.isEnabled ? item.isEnabled() : true) && hasEnabledChildren(item);

/**
 * Drops nav items whose `requiredPermission` the current user does not hold.
 *
 * Kept out of `isItemEnabled` on purpose: permissions come from React state
 * (`useAgencyPermissions`), so only a rendering surface can resolve them —
 * the getters below stay pure and hook-free.
 *
 * @param items - The already feature-flag-filtered items.
 * @param can - Predicate answering whether the user holds a permission.
 * @returns The items the user is allowed to see.
 */
export function filterByPermission(
  items: AgencyNavItem[],
  can: (permission: PermissionAction) => boolean,
): AgencyNavItem[] {
  return items.filter((item) => !item.requiredPermission || can(item.requiredPermission));
}

/**
 * Drops the modules this membership cannot open.
 *
 * The second axis: `filterByPermission` asks what the person may do, this
 * asks where. An auxiliary user carries an `operational`'s permissions and
 * still does not open the two modules the catalogue meters per paid seat.
 *
 * The item simply goes, rather than being drawn greyed out. Nobody misses
 * what they never saw, and a permanently disabled row is a standing
 * reminder of something they cannot have. Typing the URL is the case that
 * does need an answer, and the route guard gives it one.
 *
 * @param items - The nav items to filter.
 * @param canUse - Answers for one module id.
 * @returns The items this membership may open.
 */
export function filterByModule(
  items: AgencyNavItem[],
  canUse: (moduleId: string) => boolean,
): AgencyNavItem[] {
  return items.filter((item) => canUse(item.id));
}

/**
 * Comparator that orders agency nav items by ascending desktop order.
 */
const byDesktopOrder = (a: AgencyNavItem, b: AgencyNavItem) => a.desktopOrder - b.desktopOrder;
/**
 * Comparator that orders agency nav items by ascending mobile order.
 */
const byMobileOrder = (a: AgencyNavItem, b: AgencyNavItem) => a.mobileOrder - b.mobileOrder;

/**
 * Enabled desktop main-nav items (the sidebar body), sorted by desktop order.
 *
 * @returns {AgencyNavItem[]} The desktop navigation items.
 */
export function getDesktopItems(): AgencyNavItem[] {
  return AGENCY_NAV_ITEMS.filter((i) => i.desktopPlacement === 'nav' && isItemEnabled(i)).sort(byDesktopOrder);
}

/**
 * Enabled mobile tab items, sorted by mobile order.
 *
 * @returns {AgencyNavItem[]} The mobile tab items.
 */
export function getMobileTabs(): AgencyNavItem[] {
  return AGENCY_NAV_ITEMS.filter((i) => i.mobilePlacement === 'tab' && isItemEnabled(i)).sort(byMobileOrder);
}

/**
 * The enabled mobile center (highlighted) item, if any.
 *
 * @returns {AgencyNavItem | null} The center item or null when disabled/absent.
 */
export function getMobileCenter(): AgencyNavItem | null {
  return AGENCY_NAV_ITEMS.find((i) => i.mobilePlacement === 'center' && isItemEnabled(i)) ?? null;
}

/**
 * Enabled mobile "more" overflow items, sorted by mobile order.
 *
 * @returns {AgencyNavItem[]} The mobile overflow items.
 */
export function getMobileMoreItems(): AgencyNavItem[] {
  return AGENCY_NAV_ITEMS.filter((i) => i.mobilePlacement === 'more' && isItemEnabled(i)).sort(byMobileOrder);
}

/**
 * Every module the mobile shell can reach, in mobile order.
 *
 * The bar and the "Mais" sheet are two views of one list, not two lists: a
 * module the person moved into the bar has to leave the sheet, and the one
 * it displaced has to appear there. Deriving both from this makes that
 * bookkeeping impossible to get wrong, and keeps the shortcut editor from
 * offering a module that a feature flag has switched off.
 *
 * `hidden` is the one placement excluded — it means the route exists but has
 * no entry point on this surface.
 *
 * @returns {AgencyNavItem[]} Every non-hidden, enabled mobile item.
 */
export function getMobileModules(): AgencyNavItem[] {
  return AGENCY_NAV_ITEMS
    .filter((i) => i.mobilePlacement !== 'hidden' && isItemEnabled(i))
    .sort(byMobileOrder);
}

/**
 * Development-only invariant guard: asserts the registry stays well-formed so
 * divergence (duplicate ids/segments, missing or multiple center items, split
 * tab ordering) fails fast instead of silently shipping a broken nav.
 */
export function assertAgencyNavInvariants(): void {
  const ids = AGENCY_NAV_ITEMS.map((i) => i.id);
  const segments = AGENCY_NAV_ITEMS.map((i) => i.segment);
  const dupId = ids.find((id, idx) => ids.indexOf(id) !== idx);
  if (dupId) throw new Error(`[agency-nav] duplicate id: ${dupId}`);
  const dupSeg = segments.find((s, idx) => segments.indexOf(s) !== idx);
  if (dupSeg) throw new Error(`[agency-nav] duplicate segment: ${dupSeg}`);
  const centers = AGENCY_NAV_ITEMS.filter((i) => i.mobilePlacement === 'center');
  if (centers.length !== 1) throw new Error(`[agency-nav] expected exactly one 'center' item, found ${centers.length}`);
}

if (import.meta.env?.DEV) {
  assertAgencyNavInvariants();
}

/**
 * The icon a nav item shows, resized for a page header.
 *
 * Page headers used to hand-pick their own glyph, so the rail and the page it
 * opens drifted apart every time an icon changed -- Properties kept a building
 * in its header long after the rail moved to a tag. Resolving from the same
 * registry means an icon is chosen once, in one file.
 *
 * @param id - The nav item's id (its `AGENCY_NAV_ITEMS` entry).
 * @param size - Square size in px; page headers use 22, empty states 28.
 * @returns The icon element, or null when the id is unknown.
 */
export function navIconFor(id: string, size = 22): React.ReactNode {
  const item = AGENCY_NAV_ITEMS.find((i) => i.id === id);
  if (!item || !React.isValidElement(item.icon)) return null;
  return React.cloneElement(
    item.icon as React.ReactElement<{ width?: number; height?: number }>,
    { width: size, height: size },
  );
}
